import { isBefore } from 'date-fns';
import moment from 'moment';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ReactComponent as Delete } from '@assets/svg/calender_menu_delete.svg';
import { ReactComponent as Close } from '@assets/svg/calender_menu_x.svg';
import Clock from '@assets/svg/e-calendar/clock.svg';
import { ReactComponent as EditIcon } from '@assets/svg/edit_e-visit.svg';
import { actions } from '@store/ducks/reservation-form-slice';
import { EVisitItem } from 'types/e-visits';
import { MenuHeaderSC } from '../../../menu-components/menu-header/menu-header';
import ParagraphField from '../../../menu-components/paragraph-field/paragraph-field';
import TagField from '../../../tag-field/tag';
import { CommunicationArea } from '../communication-area/communication-area';
import { CalendarMenuSC } from './calendar-menu.styled';

interface CalendarMenuProps {
  item: EVisitItem;
  onClose: () => void;
  onDelete: (item: EVisitItem) => void;
  horizontalPosition?: 'left' | 'right';
}

const CalendarMenu: React.FunctionComponent<CalendarMenuProps> = ({
  item,
  onClose,
  onDelete,
  horizontalPosition,
}) => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  // @ts-expect-error wrong types
  const patientProfile = item.event.extraData?.client;
  const isPaired = patientProfile?.pairing?.status === 'paired';

  const dateFrom = moment(item.event.from);
  const dateTo = moment(item.event.to);
  const isInPast = isBefore(dateFrom.toDate(), Date.now());

  const headerActions: any = useMemo(() => {
    const _actions = [
      {
        icon: <Close />,
        onClick: onClose,
      },
    ];
    if (!isInPast) {
      _actions.unshift({
        icon: <Delete />,
        onClick: () => {
          onClose();
          onDelete(item);
        },
      });
      _actions.unshift({
        icon: <EditIcon />,
        onClick: () => {
          push('/e-visits/edit');
          dispatch(actions.setEVisitItem(item));
        },
      });
    }

    return _actions.map((action, i) => {
      return (
        <button key={i} className="action" type="button" onClick={action.onClick}>
          {React.cloneElement(action.icon)}
        </button>
      );
    });
  }, [isInPast]);

  const date = useMemo(() => {
    return `${dateFrom.format('D. MMMM HH:mm')} - ${dateTo.format('HH:mm')}`;
  }, [dateFrom, dateTo]);

  const { formatMessage } = useIntl();

  const Description = useMemo(() => {
    const doctorDescription = item.event.description;
    const patientDescription = item.order?.patientDescription;
    return (
      <>
        {doctorDescription ? (
          <>
            <div className="description-title">
              <FormattedMessage id="calendarMenu_doctor_description" />
            </div>
            <p className="description">{doctorDescription}</p>
          </>
        ) : null}
        {patientDescription ? (
          <>
            <div className="description-title">
              <FormattedMessage id="calendarMenu_description" />
            </div>
            <p className="description">{patientDescription}</p>
          </>
        ) : null}
      </>
    );
  }, [item]);

  const Title = useMemo(() => {
    let res = '';
    let opacity = 1;

    // @ts-expect-error structure change
    if (item.event.extraData.client) {
      // @ts-expect-error structure change
      res = `${item.event.extraData.client.firstName} ${item.event.extraData.client.lastName}`;
    } else if (item.event.title) {
      res = item.event.title;
    } else {
      res = formatMessage({ id: 'reservation_form_no_title' });
      opacity = 0.5;
    }

    return (
      <div className="text" style={{ opacity }}>
        {res}
      </div>
    );
  }, [item.order, item.event.title, formatMessage]);

  return (
    <CalendarMenuSC position={horizontalPosition}>
      <MenuHeaderSC horizontalPosition={horizontalPosition}>
        <div className="title-container">
          <button
            className={`title ${isPaired ? 'clickable' : ''}`}
            onClick={isPaired ? () => push(`/life-id/${patientProfile.id}`) : undefined}
          >
            {Title}
          </button>
        </div>
        <div className="actions">{headerActions}</div>
      </MenuHeaderSC>
      {item.order?.tag ? <TagField tag={item.order?.tag} /> : null}
      <div className="content">
        <ParagraphField icon={Clock} paragraph={date} />
        {Description}
      </div>
      <CommunicationArea item={item} />
    </CalendarMenuSC>
  );
};

export default React.memo<CalendarMenuProps>(CalendarMenu);
